

























import { Component, Watch } from 'vue-property-decorator'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import { Response } from '@/store/modules/survey-store-module'
import * as ColorUtils from '@/components/Survey/Utilities/ColorUtility'
import stopWords from '@/assets/json/stopWords.json'
import {
  combineLemmatizedFrequencies,
  generateLemmaDictionary,
  getFilteredNgrams,
  getNgrams
} from '@/components/Survey/Utilities/TextUtility'
import OpenEndsCard from '@/components/Survey/TextResponses/OpenEndsCard.vue'
import { BaseQuestion } from '../BaseQuestion'
import Tabs from '@/components/Tabs/Tabs.vue'
import Tab from '@/components/Tabs/Tab.vue'
import SliceNotSupported from '@/components/Survey/SliceNotSupported.vue'
import { wordCloudTooltipFormatter } from '@/components/Survey/Utilities/ChartTooltipUtilities'
import { generateChartSubtitle, getSegmentCount } from '@/components/Survey/Utilities/ChartUtilities'
import { TabData } from '@/components/Tabs/TabTypes'

export interface HighchartsPoint {
  name: string,
  color: string
}

@Component({
  components: {
    Highcharts: Chart,
    Tabs,
    Tab,
    OpenEndsCard,
    SliceNotSupported
  }
})
export default class Wordcloud extends BaseQuestion {
  maxWords: number = 100
  highcharts = Highcharts
  frequencyThreshold: number = 2
  selectedPhrase = ''

  get maxDimensions () {
    return this.isSliced || this.isVariant
  }

  get slicedCategories () {
    return Object.keys(this.slicedResponses)
  }

  get sliceSupported () {
    // If there are any words at all that meet frequency threshold, show the cloud
    return this.chartOptions.series[0].data.length > 1
  }

  get responses () {
    if (this.maxDimensions) {
      return this.typedFilteredSlicedResponses[this.selectedTab]
    } else {
      return this.filteredIndexResponses
    }
  }

  get chartOptions () {
    const series = this.wordcloudSeries(this.responses)
    return {
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        ...this.defaultChartOptions().exporting,
        chartOptions: {
          ...this.defaultChartOptions().exporting.chartOptions,
          subtitle: {
            text: this.chartSubtitle
          },
          chart: {
            events: {
              load: function () {}
            }
          }
        }
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: (event: MouseEvent & { point: HighchartsPoint }) => this.selectPhrase(event)
            }
          },
          clip: false
        }
      },
      tooltip: wordCloudTooltipFormatter(this.allWordFrequencies),
      series
    }
  }

  selectPhrase (event: MouseEvent & { point: HighchartsPoint }) {
    if (this.selectedPhrase === event.point.name) {
      this.selectedPhrase = ''
    } else {
      this.selectedPhrase = event.point.name
    }
  }

  wordcloudSeries (responses: Response[]) {
    return [{
      type: 'wordcloud',
      name: 'Occurrences',
      minFontSize: 5,
      rotation: {
        to: 0, from: 0
      },
      allowPointSelect: true,
      states: {
        select: {
          color: '#15c7a3'
        }
      },
      data: this.wordFrequencies(this.frequencyMap(this.filteredNGrams(responses)))
    }]
  }

  get colors () {
    return ColorUtils.generateWeightedColors(this.maxWords)
  }

  get lemmaDictionary () {
    return generateLemmaDictionary(this.responses)
  }

  get allWordFrequencies () {
    const nGrams = this.responses.map(response => getNgrams(response)).flat()
    return this.frequencyMap(nGrams)
  }

  filteredNGrams (responses: Response[]): string[] {
    return responses.map(response => getFilteredNgrams(response)).flat()
  }

  frequencyMap (nGrams: string[]) {
    const frequencyMap = nGrams.reduce((dataSeries: Record<string, number>, ngram) => {
      dataSeries[ngram] ? dataSeries[ngram] += 1 : dataSeries[ngram] = 1
      return dataSeries
    }, {})
    return frequencyMap
  }

  wordFrequencies (frequencyMap: Record<string, number>) {
    const useLemmaDict = Object.keys(this.lemmaDictionary).length
    const frequencies = useLemmaDict ? combineLemmatizedFrequencies(frequencyMap, this.lemmaDictionary) : frequencyMap

    return Object.entries(frequencies)
      .filter(([word, frequency]) => {
        return !!word &&
          word.length > 1 &&
          frequency >= this.frequencyThreshold &&
          !stopWords.includes(word) &&
          !this.question.text.includes(word)
      })
      .sort((a, b) => a[1] > b[1] ? -1 : 1)
      .slice(0, this.maxWords)
      .map(([word, frequency], index) => ({ name: word, weight: frequency, color: this.colors[index] }))
  }

  /**
   * Added this watch because we change the selectedTab to a client generated config label
   * which is not in the normal set of wordcloud tab labels
   */
  @Watch('typedFilteredSlicedResponses', { deep: false })
  resetSelectedTab (newResponseSet: Record<string, Response[]>) {
    if (!Object.keys(newResponseSet).includes(this.selectedTab)) {
      this.selectedTab = Object.keys(this.slicedResponses)[0]
    }
  }

  get chartSubtitle () {
    if (this.isSliced || this.isVariant) {
      const nSize = getSegmentCount(this.typedFilteredSlicedResponses[this.selectedTab])
      return generateChartSubtitle(nSize, this.sliceBy, this.selectedTab)
    }
    return ''
  }

  get tabData (): TabData[] {
    return Object.entries(this.typedFilteredSlicedResponses).map(([key, responses]) => {
      return {
        title: key,
        segmentCount: getSegmentCount(responses)
      }
    })
  }
}
